import {
  ChangeDetectionStrategy,
  Component,
  Input,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BooleanComponent } from '../../../boolean/components/boolean/boolean.component';
import { CurrencyComponent } from '../../../currency/components/currency/currency.component';
import { DateComponent } from '../../../date/components/date/date.component';
import { DateTimeComponent } from '../../../date/components/date-time/date-time.component';
import { NumberComponent } from '../../../number/components/number/number.component';
import { isEmptyString, isNotNil, Nillable } from '@web/utils';
import { TableColumn } from '../../models/table-column';
import { TableDataKey } from '../../types/table-data-key';
import { TableCustomCellTemplateContext } from '../../models/table-custom-cell-template-context';
import { TableColumnType } from '../../enums/table-column-type';
import { NoDataComponent } from '../../../no-data/components/no-data/no-data.component';
import { TableBodyCurrentPageRowSelectionCheckboxComponent } from '../table-body-current-page-row-selection-checkbox/table-body-current-page-row-selection-checkbox.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'td[app-table-body-cell]',
  imports: [
    CommonModule,
    CurrencyComponent,
    DateComponent,
    DateTimeComponent,
    NumberComponent,
    BooleanComponent,
    NoDataComponent,
    TableBodyCurrentPageRowSelectionCheckboxComponent,
  ],
  templateUrl: './table-body-cell.component.html',
  styleUrls: ['./table-body-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableBodyCellComponent<Item, Filter> {
  @Input() public order!: number;
  @Input() public column!: TableColumn<Item, Filter>;
  @Input() public model!: Item;
  @Input() public dataKey!: TableDataKey<Item>;
  @Input() public bodyRowSelectionDisabled!: boolean;
  @Input() public appendLabel!: boolean;
  /* prettier-ignore */
  @Input() public customCellTemplateRef: Nillable<TemplateRef<TableCustomCellTemplateContext<Item, Filter>>>;

  protected readonly TableColumnType = TableColumnType;

  public get amount(): number {
    return this.model[this.key] as number;
  }

  public get date(): Date {
    return this.model[this.key] as Date;
  }

  public get number(): number {
    return this.model[this.key] as number;
  }

  public get boolean(): boolean {
    return this.model[this.key] as boolean;
  }

  public get dateTime(): Date {
    return this.date;
  }

  public get currency(): string {
    return this.column.currency as string;
  }

  public get text(): string {
    return this.model[this.key] as string;
  }

  private get key(): keyof typeof this.model {
    return this.column.key as TableDataKey<Item>;
  }

  protected shouldShowCurrency(): boolean {
    return isNotNil(this.amount) && isNotNil(this.currency);
  }

  protected shouldShowNumber(): boolean {
    return isNotNil(this.number);
  }

  protected shouldShowBoolean(): boolean {
    return isNotNil(this.boolean);
  }

  protected shouldShowDate(): boolean {
    return isNotNil(this.date);
  }

  protected shouldShowDateTime(): boolean {
    return isNotNil(this.dateTime);
  }

  protected shouldShowText(): boolean {
    return isNotNil(this.text) && !isEmptyString(this.text);
  }
}
